"use client";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { LeaderboardTable, rowId } from "@/app/dashboard/_sections/LeaderboardTable";
import { MyMinerStrip } from "@/app/dashboard/_sections/MyMinerStrip";
import { dashboardContent, fmt } from "@/lib/content";
import { leaderboardScales, topScoredHotkey, type LeaderboardRow } from "@/lib/dashboard/rows";
import { ALL_TAB, locateMiner } from "@/lib/dashboard/table";
import { useLeaderboard } from "@/lib/hooks/useLeaderboard";
import { useMyHotkey } from "@/lib/hooks/useMyHotkey";
import { cn } from "@/lib/utils";

export function Leaderboard({
  rows,
  columns,
  compId,
  withheld = false,
  toolbarPicker,
}: {
  rows: LeaderboardRow[];
  columns: string[];
  compId: number | null;
  /** The backend declined this competition's results; say so rather than "none yet". */
  withheld?: boolean;
  /**
   * The competition picker lives in the page heading now. Passing it here puts it back at
   * the end of the toolbar, where it used to be - nothing else has to change.
   */
  toolbarPicker?: ReactNode;
}) {
  const t = dashboardContent.leaderboard;
  const board = useLeaderboard(rows);
  // Both are measured over the whole board, not the visible page: the colour of a score
  // must not change because someone typed into the search box.
  const scales = useMemo(() => leaderboardScales(rows, columns), [rows, columns]);
  const topHotkey = useMemo(() => topScoredHotkey(rows), [rows]);
  const mine = useMyHotkey();
  const followed = mine.hotkey;
  const position = useMemo(() => locateMiner(rows, followed), [rows, followed]);

  // Scrolling has to wait for the page switch to render the row; the hotkey waiting to be
  // scrolled to is the signal, cleared once it has been done.
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingScroll) return;
    document.getElementById(rowId(pendingScroll))?.scrollIntoView({ block: "center", behavior: "smooth" });
    setPendingScroll(null);
  }, [pendingScroll, board.page]);

  return (
    <section className="grid gap-4">
      <h2 className="eyebrow text-fg-muted">{t.title}</h2>

      <div className="grid gap-5 rounded-md border border-line px-6 py-5">
        {/* One line: the chips on the left, the search held at a fixed width on the right.
            The chips wrap inside their own box, so a count that grows never pushes the
            search field into the middle of the run; on a phone the search drops below. */}
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {board.tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                aria-pressed={board.tab === tab.id}
                onClick={() => board.setTab(tab.id)}
                className={cn(
                  "cursor-pointer rounded-full px-3.5 py-1.5 text-[13.5px] transition-colors",
                  board.tab === tab.id ? "bg-btn text-btn-fg" : "text-fg-muted hover:text-fg",
                )}
              >
                {tab.id === ALL_TAB ? t.all : (t.statusLabels[tab.id] ?? tab.id)} ({tab.count})
              </button>
            ))}
          </div>

          <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
            <input
              type="search"
              value={board.query}
              onChange={(e) => board.setQuery(e.target.value)}
              placeholder={t.search}
              aria-label={t.search}
              className="h-9 w-full rounded-full border border-line bg-transparent px-4 text-[14px] outline-none placeholder:text-fg-faint hover:border-fg-muted focus-visible:border-fg-muted sm:w-60"
            />
            {toolbarPicker && <div className="w-full sm:w-64">{toolbarPicker}</div>}
          </div>
        </div>

        {followed && (
          <MyMinerStrip
            hotkey={followed}
            position={position}
            onShow={() => {
              if (board.reveal(followed)) setPendingScroll(followed);
            }}
            onForget={mine.clear}
          />
        )}

        {board.page.total === 0 ? (
          <p className="py-6 text-[14px] text-fg-muted">
            {rows.length === 0 ? (withheld ? t.withheld : t.empty) : fmt(t.noMatch, { query: board.query })}
          </p>
        ) : (
          <>
            {/* Full bleed inside the card, so the row rules and the hover band run to
                its edges instead of stopping 24px short; the gutter moves into the first
                and last cells. The overflow is a safety net, not the layout: the table
                sheds columns to fit from 375px up and only scrolls below that. */}
            <div className="-mx-6 overflow-x-auto">
              <LeaderboardTable
                rows={board.page.items}
                columns={columns}
                scales={scales}
                topHotkey={topHotkey}
                compId={compId}
                sortKey={board.sortKey}
                sortDirection={board.sortDirection}
                onSort={board.toggleSort}
              />
            </div>
            {board.page.pages > 1 && (
              <div className="flex items-center justify-between gap-4">
                <span className="font-sans tabular-nums text-[11.5px] text-fg-faint">
                  {fmt(t.page, { page: board.page.page, pages: board.page.pages })}
                </span>
                <div className="flex items-center gap-1">
                  <PageButton
                    onClick={() => board.setPage(board.page.page - 1)}
                    disabled={board.page.page === 1}
                    label="←"
                  />
                  <PageButton
                    onClick={() => board.setPage(board.page.page + 1)}
                    disabled={board.page.page === board.page.pages}
                    label="→"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function PageButton({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "grid size-8 cursor-pointer place-items-center rounded-full border border-line transition-colors",
        disabled ? "cursor-default text-fg-faint opacity-40" : "hover:border-fg-muted",
      )}
    >
      <span aria-hidden>{label}</span>
    </button>
  );
}
